import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import {
	type HttpCreateSubgeoParams,
	httpCreateSubgeo
} from '@/features/subgeo/http/create-subgeo.http.ts'
import type { HttpGetSubgeosResponse } from '@/features/subgeo/http/get-subgeos.http'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import type { ApiSubgeo, Subgeo } from '@/features/subgeo/types.ts'
import {
	type ColorPair,
	generateSingleSubgeoColor
} from '@/features/subgeo/utils/color-utils'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'

export const useCreateSubgeo = (
	options?: UseMutationOptions<
		ApiSubgeo,
		unknown,
		HttpCreateSubgeoParams['payload']
	>
) => {
	const queryClient = useQueryClient()

	return useMutation({
		...options,
		mutationFn: (payload) =>
			httpCreateSubgeo({
				payload,
				params: {
					responseType: 'extended'
				}
			}),
		onMutate: (payload) => {
			const tempId = `temp-${Date.now()}`
			const currentState = useSubgeoStore.getState()

			const optimisticColor = generateSingleSubgeoColor(
				tempId,
				currentState.subgeoColorMap
			)

			const optimisticSubgeo = {
				id: tempId,
				name: payload.name,
				neighborhoodIds: payload.neighborhoodIds,
				color: optimisticColor.fill
			}

			const currentSubgeos = currentState.subgeos
			const updatedSubgeos = currentSubgeos.map((s) => ({
				...s,
				neighborhoodIds: s.neighborhoodIds.filter(
					(id) => !payload.neighborhoodIds.includes(id)
				)
			}))

			const newColorMap = new Map(currentState.subgeoColorMap)
			newColorMap.set(tempId, optimisticColor)

			useSubgeoStore.setState({
				subgeos: [...updatedSubgeos, optimisticSubgeo],
				subgeoColorMap: newColorMap
			})

			return {
				previousSubgeos: currentSubgeos,
				previousColorMap: currentState.subgeoColorMap,
				tempId,
				optimisticColor
			}
		},
		onError: (error, _variables, mutationContext) => {
			if (
				mutationContext &&
				typeof mutationContext === 'object' &&
				'previousSubgeos' in mutationContext &&
				'previousColorMap' in mutationContext
			) {
				useSubgeoStore.setState({
					subgeos: mutationContext.previousSubgeos as Subgeo[],
					subgeoColorMap: mutationContext.previousColorMap as Map<
						string,
						ColorPair
					>
				})
			}
			errorHandler(error)
		},
		onSuccess: (data, variables, context, mutationContext) => {
			if (context?.tempId && context?.optimisticColor) {
				const currentState = useSubgeoStore.getState()

				const newSubgeo: Subgeo = {
					id: data.id,
					name: data.name,
					neighborhoodIds: variables.neighborhoodIds,
					color: context.optimisticColor.fill
				}

				const updatedSubgeos = currentState.subgeos
					.filter((s) => s.id !== context.tempId)
					.concat(newSubgeo)

				const newColorMap = new Map(currentState.subgeoColorMap)
				newColorMap.delete(context.tempId)
				newColorMap.set(data.id, context.optimisticColor)

				useSubgeoStore.setState({
					subgeos: updatedSubgeos,
					subgeoColorMap: newColorMap
				})

				const queryKey = [
					QUERY_KEYS.GET_SUBGEOS,
					{
						geoId: data.geoMappingGeoId,
						responseType: 'extended'
					}
				]

				queryClient.setQueryData<HttpGetSubgeosResponse>(
					queryKey,
					(old) => {
						if (!old) {
							return old
						}

						return {
							...old,
							data: [...old.data, data]
						}
					}
				)
			}

			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_NEIGHBORHOODS]
			})
			options?.onSuccess?.(data, variables, context, mutationContext)
		}
	})
}
